import React from 'react';
import { Link } from 'react-router-dom';
import { ChartLine, Bank, Bell, ShieldCheck, Files, ArrowsLeftRight } from '@phosphor-icons/react';
import PublicSiteLayout from '../components/PublicSiteLayout';

export default function PublicLandingPage() {
  return (
    <PublicSiteLayout>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="grid lg:grid-cols-[1.15fr_0.85fr] gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-white border border-[#E8E6E1] rounded-full px-4 py-2 text-sm text-[#5C5A56] shadow-sm">
              <Bell size={14} weight="fill" className="text-[#A63C24]" />
              Plan cash, bills, EMI, investments and alerts in one place
            </div>
            <h1 className="mt-6 text-4xl md:text-6xl font-extrabold tracking-tight leading-[1.02]" style={{ fontFamily: 'Manrope, sans-serif' }}>
              A clean finance workspace for your daily money decisions.
            </h1>
            <p className="mt-5 text-lg text-[#5C5A56] max-w-2xl leading-8">
              Track transactions, bank and cash balances, NEPSE investments, EMI schedules, payments, lending, borrowing, and printable reports from one dashboard.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Link to="/login" className="inline-flex items-center justify-center px-6 py-3.5 rounded-2xl bg-[#1E3F2D] text-white font-semibold hover:bg-[#29563E] transition-colors">
                Login To Continue
              </Link>
              <Link to="/features" className="inline-flex items-center justify-center px-6 py-3.5 rounded-2xl bg-white border border-[#E8E6E1] text-[#1C1B1A] font-semibold hover:bg-[#F5F1EB] transition-colors">
                Explore Features
              </Link>
            </div>
          </div>

          <div className="relative">
            <div className="absolute -top-6 -right-6 w-32 h-32 bg-[#D98324]/10 rounded-full blur-3xl" />
            <div className="absolute -bottom-8 -left-4 w-40 h-40 bg-[#1E3F2D]/10 rounded-full blur-3xl" />
            <div className="relative bg-white border border-[#E8E6E1] rounded-[28px] shadow-[0_20px_60px_rgba(30,63,45,0.08)] p-6 space-y-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#7A766E]">Monthly Snapshot</p>
                  <p className="text-2xl font-bold mt-2">Know where your money stands</p>
                </div>
                <div className="w-12 h-12 rounded-2xl bg-[#1E3F2D] flex items-center justify-center">
                  <ChartLine size={22} weight="fill" className="text-white" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <InfoCard label="Net Worth" value="Live summary" tone="green" />
                <InfoCard label="EMI & Bills" value="Due reminders" tone="amber" />
                <InfoCard label="Accounts" value="Bank / Cash / Wallet" tone="green" />
                <InfoCard label="Reports" value="Date-wise PDF" tone="amber" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <MiniFeature icon={ArrowsLeftRight} title="Transactions" />
                <MiniFeature icon={Bank} title="Accounts" />
                <MiniFeature icon={Files} title="Reports" />
                <MiniFeature icon={ShieldCheck} title="Reminders" />
              </div>
            </div>
          </div>
        </div>
      </section>
    </PublicSiteLayout>
  );
}

function InfoCard({ label, value, tone }) {
  const toneClasses = tone === 'amber'
    ? 'bg-[#FFF5E9] text-[#A86512]'
    : 'bg-[#EEF6F1] text-[#1E3F2D]';

  return (
    <div className={`rounded-2xl p-4 ${toneClasses}`}>
      <p className="text-xs font-bold uppercase tracking-[0.14em] opacity-70">{label}</p>
      <p className="text-base font-semibold mt-2">{value}</p>
    </div>
  );
}

function MiniFeature({ icon: Icon, title }) {
  return (
    <div className="bg-[#F8F5F0] border border-[#E8E6E1] rounded-2xl p-4 flex items-center gap-3">
      <div className="w-10 h-10 rounded-xl bg-[#1E3F2D]/10 flex items-center justify-center">
        <Icon size={18} weight="fill" className="text-[#1E3F2D]" />
      </div>
      <div>
        <p className="text-sm font-semibold text-[#1C1B1A]">{title}</p>
        <p className="text-xs text-[#7A766E]">Structured and easy to review</p>
      </div>
    </div>
  );
}
